import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import useOrderedCustomRequests from '../useOrderedCustomRequests';

export default function useCustomKybRequests(vehicleId: string) {
  const queryKey = getQueryKey(
    api.onboardingCustomRequest.listKybCustomRequests,
    { vehicleId },
    'query',
  );

  const { data: customKybRequests, isLoading: isLoadingCustomKybRequests } =
    api.onboardingCustomRequest.listKybCustomRequests.useQuery({
      vehicleId,
    });

  const { orderedCustomRequests, setCustomRequestsOrder } =
    useOrderedCustomRequests(customKybRequests ?? []);

  const { mutate: addRequest, isPending: isAddingRequest } =
    api.onboardingCustomRequest.addKybCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request added successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: updateRequest, isPending: isUpdatingRequest } =
    api.onboardingCustomRequest.updateKybCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request updated successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: deleteRequest, isPending: isDeletingRequest } =
    api.onboardingCustomRequest.deleteKybCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request deleted successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: reorderKybCustomRequests } =
    api.onboardingCustomRequest.reorderKybCustomRequests.useMutation(
      useHandleTrpcMutation()({
        invalidations: [queryKey],
        onSuccess: () => {
          setCustomRequestsOrder([]);
        },
      }),
    );

  const handleCustomRequestsOrderChange = (orderedItemIds: Array<string>) => {
    setCustomRequestsOrder(orderedItemIds);
    reorderKybCustomRequests({
      vehicleId,
      orderedItemIds,
    });
  };

  const isLoading =
    isLoadingCustomKybRequests ||
    isAddingRequest ||
    isUpdatingRequest ||
    isDeletingRequest;

  return {
    customKybRequests: orderedCustomRequests,
    handleCustomRequestsOrderChange,
    addCustomKybRequest: addRequest,
    updateCustomKybRequest: updateRequest,
    deleteCustomKybRequest: deleteRequest,
    isLoading,
  };
}
